import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import {
  BackupDTabFormStructure,
  FormKey,
  FormKey as FormKeyBackup
} from '@common/components/my-account/tabs/backup-destination-tab/backup-destination-tab.model';
import { IsAvailableRegionPipe } from '@common/components/my-account/tabs/backup-destination-tab/help-pipes/is-available-region.pipe';
import {
  EndpointAccess,
  FormBuilderService
} from '@common/components/my-account/tabs/backup-destination-tab/help-services/form-builder.service';
import { UiDataService } from '@common/components/my-account/tabs/backup-destination-tab/help-services/ui-data.service';
import { BackupDestinationComponent } from '@common/components/storage-settings-form/storage-settings-form.component';
import { SECRET_KEY_MASK } from '@common/constants';
import { BackupDestination, StorageState, StorageType } from '@common/models';
import { MyAccountService } from '@common/services';
import { AuthEffectService } from '@common/services/auth/auth-effect.service';
import { capitalizeObjectKeys, getErrorText, hasErrorResponseText, trackModalRequest } from '@common/utils';
import { I18NextPipe } from 'angular-i18next';
import { I18_NAMESPACE_APPS_UI } from 'i18n';
import isEqual from 'lodash/isEqual';
import {
  AlertComponent,
  ButtonComponent,
  FormSectionComponent,
  MbsPopupType,
  MbsSize,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  ModalSettings,
  ToastService
} from 'mbs-ui-kit';
import { defer, EMPTY, from, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';

const components = [AlertComponent, BackupDestinationComponent, ButtonComponent, FormSectionComponent, ModalComponent];
const directives = [ModalFooterDirective];
const pipes = [I18NextPipe, IsAvailableRegionPipe];

@Component({
  imports: [...components, ...directives, ...pipes],
  providers: [FormBuilderService, UiDataService],
  templateUrl: './storage-settings-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StorageSettingsModalComponent implements OnInit {
  private readonly authEffect = inject(AuthEffectService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fbService = inject(FormBuilderService);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly modalService = inject(ModalService);
  private readonly myAccountService = inject(MyAccountService);
  private readonly toastService = inject(ToastService);
  private readonly uiDataService = inject(UiDataService);

  private readonly trackRequestFn = trackModalRequest();

  private readonly baseModal = viewChild.required(ModalComponent);

  private readonly backupDConfirmTRef = viewChild('backupDConfirmTRef', { read: TemplateRef<unknown> });

  readonly #toastTitleSuccess = this.i18nPipe.transform('toast.success.title', { format: 'capitalize' });
  readonly #toastTitleError = this.i18nPipe.transform('toast.error.title', { format: 'capitalize' });
  readonly #toastTitleWarning = this.i18nPipe.transform('toast.warning.title', { format: 'capitalize' });

  protected readonly moduleAccount = I18_NAMESPACE_APPS_UI.account;
  protected readonly moduleButton = I18_NAMESPACE_APPS_UI.button;
  protected readonly MbsPopupType = MbsPopupType;

  protected readonly formGroup = this.fbService.buildForm();

  protected readonly loading = signal<boolean>(true);
  protected readonly buttonLoading = signal<boolean>(false);
  protected readonly verified = signal<boolean>(false);
  protected readonly testLoading = signal<boolean>(false);

  protected readonly initialSettings = signal<BackupDTabFormStructure>(null);
  protected readonly storages = toSignal(
    defer(() => {
      this.loading.set(true);
      return this.myAccountService.getStorages().pipe(finalize(() => this.loading.set(false)));
    }),
    { initialValue: [] }
  );

  protected readonly storagesEffect = effect(() => {
    const current = this.storageTypeCtrl.value;
    const defaultStorage = this.storages().find((s) => s.IsDefault)?.Name;

    if (this.storageTypeCtrl.value !== defaultStorage) {
      this.storageTypeCtrl.patchValue(current || defaultStorage, { emitEvent: false });
    }

    this.fbService.checkExtraControls(this.formGroup, this.endpointAccess);
  });

  get storageTypeCtrl(): FormControl<StorageType> {
    return this.formGroup.controls[FormKey.StorageType];
  }

  get endpointAccess(): EndpointAccess {
    const type = this.storageTypeCtrl.value;
    const isRegion = this.uiDataService.isAvailableRegion(type);

    return {
      [FormKey.AccessKey]: !isRegion,
      [FormKey.BucketName]: !isRegion,
      [FormKey.CurrentSizeBytes]: isRegion,
      [FormKey.DeleteDate]: isRegion,
      [FormKey.Endpoint]: !isRegion && this.uiDataService.isAvailableEndpoint(type),
      [FormKey.EndpointSuffix]: !isRegion && this.uiDataService.isAvailableEndpointSuffix(type),
      [FormKey.Name]: !isRegion,
      [FormKey.Region]: isRegion,
      [FormKey.RootFolder]: !isRegion,
      [FormKey.SecretKey]: !isRegion,
      [FormKey.StorageType]: true
    };
  }

  ngOnInit(): void {
    this.initStreams();
  }

  initStreams(): void {
    this.storageTypeCtrl.valueChanges
      .pipe(distinctUntilChanged(isEqual), tap(this.patchFormGroup.bind(this)), takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private patchFormGroup(storageType: StorageType): void {
    const exceptions = {
      [FormKey.Region]: null,
      [FormKey.SecretKey]: null,
      [FormKey.StorageType]: storageType
    };

    this.formGroup.patchValue(exceptions, { emitEvent: false });
    this.fbService.checkExtraControls(this.formGroup, this.endpointAccess);
  }

  handleSave(): void {
    const payload = this.buildBackupDestinationPayload();

    this.buttonLoading.set(true);
    this.testSavingStorage(payload)
      .pipe(
        switchMap((testResult) => {
          if (testResult) return this.setStorage(payload);
          // saving an unverified storage needs the user's confirmation, which a closed modal can no longer ask for
          if (this.destroyRef.destroyed) return EMPTY;

          return this.showBackupConfirmModal().pipe(
            catchError(() => of(false)),
            filter(Boolean),
            switchMap(() => this.setStorage(payload))
          );
        }),
        take(1),
        finalize(() => this.buttonLoading.set(false)),
        this.trackRequestFn
      )
      .subscribe({
        next: () => {
          this.toastService.success(
            this.i18nPipe.transform(this.moduleAccount + ':toast.body.saveBackupDestination', { format: 'capitalize' }),
            this.#toastTitleSuccess
          );
          this.baseModal().save(true);
        },
        error: (res: HttpErrorResponse) => hasErrorResponseText(res) && this.toastService.error(getErrorText(res), this.#toastTitleError)
      });
  }

  private buildBackupDestinationPayload(): BackupDestination {
    const normalize = <T>(value: T | undefined): T | null => (value === undefined ? null : value);
    const formData = this.formGroup.value;
    const isRegionAvailable = this.uiDataService.isAvailableRegion(this.storageTypeCtrl.value);
    const SecretKey = formData[FormKeyBackup.SecretKey] === SECRET_KEY_MASK ? null : normalize(formData[FormKeyBackup.SecretKey]);
    const basePayload = {
      AccessKey: normalize(formData[FormKeyBackup.AccessKey]),
      BucketName: normalize(formData[FormKeyBackup.BucketName]),
      Name: normalize(formData[FormKeyBackup.Name]),
      RootFolder: normalize(formData[FormKeyBackup.RootFolder]),
      SecretKey,
      StorageType: normalize(formData[FormKeyBackup.StorageType]),
      Endpoint: normalize(formData[FormKeyBackup.Endpoint]),
      EndpointSuffix: normalize(formData[FormKeyBackup.EndpointSuffix])
    } as BackupDestination;

    return isRegionAvailable ? { ...basePayload, Region: formData[FormKeyBackup.Region] } : basePayload;
  }

  private testSavingStorage(payload: BackupDestination): Observable<boolean> {
    return this.uiDataService.isAvailableRegion(this.storageTypeCtrl.value)
      ? of(true)
      : this.myAccountService.testStorage(payload).pipe(map((result) => result.value === StorageState.Ok));
  }

  private setStorage(payload: BackupDestination): Observable<boolean> {
    return this.myAccountService.setStorage(payload).pipe(
      switchMap(() => this.authEffect.getSystemInfo(true)),
      map(() => true)
    );
  }

  private showBackupConfirmModal(): Observable<boolean> {
    const settings: ModalSettings = {
      header: {
        title: this.i18nPipe.transform(this.moduleAccount + ':modal.title.backupDestinationToSaveNotFound', { format: 'capitalize' })
      },
      size: MbsSize.sm,
      footer: {
        okButton: {
          text: this.i18nPipe.transform(this.moduleButton + ':button.save', { format: 'capitalize' })
        },
        cancelButton: {
          text: this.i18nPipe.transform(this.moduleButton + ':button.discard', { format: 'capitalize' })
        }
      }
    };

    return from(this.modalService.open(settings, this.backupDConfirmTRef()));
  }

  handleTest(): void {
    const formValue = this.formGroup.value;
    const payload = {
      ...formValue,
      [FormKey.SecretKey]: formValue[FormKey.SecretKey] === SECRET_KEY_MASK ? null : formValue[FormKey.SecretKey]
    };

    this.testLoading.set(true);
    this.myAccountService
      .testStorage(capitalizeObjectKeys<BackupDestination>(payload))
      .pipe(
        map((result) => result.value === StorageState.Ok),
        tap((state) => this.verified.set(state)),
        finalize(() => this.testLoading.set(false)),
        this.trackRequestFn
      )
      .subscribe({
        next: (state) => {
          const storage = this.storages().find((s) => s.Name === this.storageTypeCtrl.value)?.DisplayName;

          if (state) {
            this.toastService.success(
              this.i18nPipe.transform(this.moduleAccount + ':toast.body.test.success', { format: 'capitalize', storage }),
              this.#toastTitleSuccess
            );
          } else {
            this.toastService.warn(
              this.i18nPipe.transform(this.moduleAccount + ':toast.body.test.warning', { format: 'capitalize', storage }),
              this.#toastTitleWarning
            );
          }
        },
        error: () => {
          this.toastService.error(
            this.i18nPipe.transform(this.moduleAccount + ':toast.body.test.error', { format: 'capitalize' }),
            this.#toastTitleError
          );
        }
      });
  }

  handleCancel(): void {
    this.baseModal().close();
  }
}
